import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Phone, Calendar, ChevronRight, Shield, Users, Clock, Award,
  Paintbrush, Home as HomeIcon, Hammer, Wrench, Zap, Grid3X3,
  Star, CheckCircle, ArrowRight, MessageCircle, Sparkles, DollarSign
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";

const HERO_BG = "/images/hero-bg.jpg";

const servicesPreview = [
  { name: "Interior Paint", icon: Paintbrush, desc: "Transform your space" },
  { name: "Flooring", icon: Grid3X3, desc: "Hardwood, laminate, tile" },
  { name: "Drywall", icon: HomeIcon, desc: "Repair & installation" },
  { name: "Roof Repair", icon: HomeIcon, desc: "Leak fixes, shingles" },
  { name: "Cabinet Install", icon: Hammer, desc: "Kitchen & bath" },
  { name: "Minor Electrical", icon: Zap, desc: "Fixtures & outlets" },
];

const trustFeatures = [
  { icon: Shield, title: "Licensed & Insured", desc: "Full coverage protection" },
  { icon: Users, title: "Family Owned", desc: "Husband & wife team" },
  { icon: Clock, title: "Since 2019", desc: "5+ years serving FL" },
  { icon: Award, title: "Quality Guaranteed", desc: "Done right, first time" },
];

const quickServices = [
  "Drywall", "Interior Paint", "Exterior Paint", "Flooring",
  "Tile", "Cabinets", "Roof Repair", "Doors/Windows", "Electrical", "Other"
];

const HomePage = () => {
  useEffect(() => { document.title = "New Leaf PMC LLC | Professional Handyman & Renovation Services"; }, []);
  const [quickForm, setQuickForm] = useState({ name: "", phone: "", service: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchGalleryImages();
    fetchTestimonials();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setGalleryImages(response.data.slice(0, 4));
    } catch (error) {
      console.error("Error fetching gallery:", error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/reviews/public`);
      setTestimonials(response.data.length > 0 ? response.data.slice(0, 6) : []);
    } catch {
      setTestimonials([]);
    }
  };

  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    if (!quickForm.name || !quickForm.phone || !quickForm.service) {
      toast.error("Please fill in name, phone, and service");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, { ...quickForm, email: "", message: `Quick form: ${quickForm.service}` });
      toast.success("We'll call you within minutes!");
      setQuickForm({ name: "", phone: "", service: "" });
      // Track conversion
      if (window._track) window._track("lead_submit", { source: "homepage_quick_form", service: quickForm.service });
    } catch (error) {
      toast.error("Something went wrong. Call us at 863-340-9244");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-16 md:pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="Modern home renovation" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 hero-overlay"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-3xl animate-fade-in">
            {/* Urgency badge */}
            <div className="inline-flex items-center space-x-2 bg-[#FF6B35]/20 border border-[#FF6B35]/40 text-[#FF6B35] px-4 py-1.5 rounded-full text-sm font-medium mb-6" data-testid="urgency-badge">
              <Sparkles size={14} />
              <span>Limited availability this week — book today</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6">
              Reliable Home Repairs in Central Florida — <span className="gradient-text">Done Right the First Time</span>
            </h1>
            <p className="text-lg md:text-xl text-[#A0AAB2] mb-8">
              Family-owned. Fast, professional, and trusted since 2019.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/booking"
                className="inline-flex items-center justify-center space-x-2 gradient-btn px-8 py-4 rounded-xl font-bold text-lg shadow-lg shadow-[#FF6B35]/20"
                data-testid="hero-book-btn"
              >
                <Calendar size={22} />
                <span>Get a Free Estimate</span>
              </Link>
              <a
                href="tel:863-340-9244"
                className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/20 transition-all"
                data-testid="hero-call-btn"
              >
                <Phone size={22} />
                <span>Call Now</span>
              </a>
            </div>

            {/* Social proof mini */}
            <div className="flex items-center space-x-3 mt-8">
              <div className="flex -space-x-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-[#4A2B6E] to-[#FF6B35] border-2 border-[#0B0C10] flex items-center justify-center text-white text-xs font-bold">
                    {["M", "J", "L", "R"][i]}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
                </div>
                <p className="text-[#A0AAB2] text-xs">Trusted by Central Florida homeowners</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-[#15161C] py-10 md:py-14 border-y border-[#2A2B32]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {trustFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="w-14 h-14 rounded-xl bg-[#1F2027] border border-[#2A2B32] flex items-center justify-center mx-auto mb-3">
                    <Icon size={24} className="text-[#FF6B35]" />
                  </div>
                  <h3 className="text-white font-semibold text-sm md:text-base">{feature.title}</h3>
                  <p className="text-[#A0AAB2] text-xs md:text-sm">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Lead Form */}
      <section className="py-14 md:py-20 bg-[#0B0C10]" data-testid="quick-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-2xl p-6 md:p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Get Your Free Estimate</h2>
              <p className="text-[#A0AAB2] text-sm flex items-center justify-center space-x-2">
                <Clock size={14} className="text-green-400" />
                <span>Get a response within minutes</span>
              </p>
            </div>
            <form onSubmit={handleQuickSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={quickForm.name}
                  onChange={(e) => setQuickForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3.5 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                  placeholder="Your Name *"
                  data-testid="quick-form-name"
                />
                <input
                  type="tel"
                  value={quickForm.phone}
                  onChange={(e) => setQuickForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3.5 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                  placeholder="Phone Number *"
                  data-testid="quick-form-phone"
                />
              </div>
              <select
                value={quickForm.service}
                onChange={(e) => setQuickForm(prev => ({ ...prev, service: e.target.value }))}
                className="w-full px-4 py-3.5 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white focus:outline-none focus:border-[#FF6B35]"
                data-testid="quick-form-service"
              >
                <option value="">What do you need done? *</option>
                {quickServices.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full gradient-btn py-4 rounded-xl font-bold text-lg disabled:opacity-50 shadow-lg shadow-[#FF6B35]/20"
                data-testid="quick-form-submit"
              >
                {isSubmitting ? "Sending..." : "Get My Free Estimate"}
              </button>
              <p className="text-center text-[#A0AAB2]/60 text-xs">No spam. We only use your info to contact you about your project.</p>
            </form>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-14 md:py-20 bg-[#15161C]" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">What We Do</h2>
            <p className="text-[#A0AAB2] text-sm md:text-base max-w-2xl mx-auto">
              From minor repairs to complete renovations, we handle it all.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {servicesPreview.map((service, idx) => {
              const Icon = service.icon;
              return (
                <Link
                  key={idx}
                  to="/booking"
                  className="group bg-[#1F2027] border border-[#2A2B32] rounded-xl p-5 hover:border-[#FF6B35]/50 transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#4A2B6E]/20 to-[#0D5C63]/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <Icon size={22} className="text-[#FF6B35]" />
                  </div>
                  <h3 className="text-white font-semibold mb-1 text-sm md:text-base">{service.name}</h3>
                  <p className="text-[#A0AAB2] text-xs md:text-sm">{service.desc}</p>
                  <div className="flex items-center space-x-1 mt-2 text-[#FF6B35] text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Get estimate</span>
                    <ArrowRight size={12} />
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <Link to="/services" className="inline-flex items-center space-x-2 text-[#FF6B35] hover:text-white font-semibold transition-colors text-sm" data-testid="view-all-services-btn">
              <span>View All Services</span>
              <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials - Only if reviews exist */}
      {testimonials.length > 0 && (
      <section className="py-14 md:py-20 bg-[#0B0C10]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">What Our Clients Say</h2>
            <p className="text-[#A0AAB2] text-sm">Real reviews from real homeowners</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((t, idx) => (
              <div key={idx} className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6" data-testid={`testimonial-${idx}`}>
                <div className="flex text-yellow-400 mb-3">
                  {[...Array(t.rating || 5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                </div>
                <p className="text-white text-sm leading-relaxed mb-4">"{t.message || t.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold text-sm">{t.name}</p>
                    {t.location && <p className="text-[#A0AAB2] text-xs">{t.location}</p>}
                  </div>
                  {t.service && <span className="text-xs px-2 py-1 bg-[#FF6B35]/20 text-[#FF6B35] rounded-full">{t.service}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Gallery Teaser - Only real images */}
      {galleryImages.length > 0 && (
      <section className="py-14 md:py-20 bg-[#15161C]" data-testid="gallery-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Our Work Speaks for Itself</h2>
            <p className="text-[#A0AAB2] text-sm">See some of our recent projects</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((image, idx) => (
              <div key={idx} className="aspect-square rounded-xl overflow-hidden border border-[#2A2B32] group">
                <img src={image.image_data} alt={image.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/gallery" className="inline-flex items-center space-x-2 text-[#FF6B35] hover:text-white font-semibold transition-colors text-sm" data-testid="view-gallery-btn">
              <span>View Full Gallery</span>
              <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* Financing CTA */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-[#0D5C63]/30 to-[#4A2B6E]/30 border-y border-[#2A2B32]" data-testid="financing-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-green-500/20 text-green-400 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <DollarSign size={16} />
            <span>Financing Available</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Financing Available — Get Started Today</h2>
          <p className="text-[#A0AAB2] text-base mb-6 max-w-2xl mx-auto">
            Don't let budget hold you back. We offer flexible financing options for your home improvement projects. Get the work you need done now and pay over time.
          </p>
          <button onClick={() => window.open("tel:863-340-9244")} data-testid="financing-cta-btn"
            className="inline-flex items-center space-x-2 gradient-btn px-8 py-4 rounded-xl font-bold text-lg shadow-lg shadow-[#FF6B35]/20">
            <span>Apply for Financing</span>
            <ArrowRight size={20} />
          </button>
          <p className="text-[#A0AAB2]/60 text-xs mt-3">Call 863-340-9244 to discuss your financing options</p>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-14 md:py-20 bg-[#0B0C10]" data-testid="why-choose-us">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Why Choose New Leaf PMC</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: "Family Owned & Operated", desc: "Husband and wife team. We treat every home like our own." },
              { title: "Serving Central Florida Since 2019", desc: "Deep roots in Polk County — Lakeland, Winter Haven, Haines City, and beyond." },
              { title: "Fast Turnaround", desc: "We respect your time. Most projects completed on schedule, no excuses." },
              { title: "Reliable & Professional", desc: "Clean work. Clear communication. No surprises on your bill." },
              { title: "Free On-Site Estimates", desc: "We come to you. See the job, give an honest quote. No pressure." },
              { title: "Quality Guaranteed", desc: "We stand behind every job. If it's not right, we make it right." },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start space-x-3 bg-[#15161C] border border-[#2A2B32] rounded-xl p-5">
                <CheckCircle size={20} className="text-green-400 mt-0.5 shrink-0" />
                <div>
                  <h3 className="text-white font-semibold text-sm mb-1">{item.title}</h3>
                  <p className="text-[#A0AAB2] text-xs leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#4A2B6E]/30 via-[#0D5C63]/30 to-[#FF6B35]/30 border-y border-[#2A2B32]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Book Today to Secure Your Spot</h2>
          <p className="text-[#A0AAB2] mb-8 text-base md:text-lg">Schedule a free estimate and let us bring your vision to life.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/booking" className="inline-flex items-center justify-center space-x-2 gradient-btn px-10 py-4 rounded-xl font-bold text-lg shadow-lg shadow-[#FF6B35]/20" data-testid="cta-book-btn">
              <Calendar size={22} />
              <span>Get a Free Estimate</span>
            </Link>
            <a href="tel:863-340-9244" className="inline-flex items-center justify-center space-x-2 bg-white/10 border border-white/20 text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-white/20 transition-all" data-testid="cta-call-btn">
              <Phone size={22} />
              <span>863-340-9244</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
